// The public price grid, ported from the website (UnanimityWebV2/src/pricing.ts,
// its single source — Kevin 29/10: « use the same price grid as website »).
// A number changes there first, then here, with the checkpoints of
// ShopifyBillingTest.test.js. Amounts are € HT per month.

export const BASE_PRICE = 450;
export const INCLUDED_TICKETS = 300;
export const QUOTE_THRESHOLD = 5000;
// Marginal price per ticket above the included tickets, by cumulative volume.
export const TICKET_TIERS = [
  { upTo: 1000, price: 1.5 },
  { upTo: 3000, price: 1.0 },
  { upTo: 5000, price: 0.8 },
];

export function additionalTicketCost(tickets) {
  let cost = 0;
  let prevCap = INCLUDED_TICKETS;
  for (const tier of TICKET_TIERS) {
    if (tickets <= prevCap) break;
    cost += (Math.min(tickets, tier.upTo) - prevCap) * tier.price;
    prevCap = tier.upTo;
  }
  return cost;
}

/** Monthly subscription excl. VAT; volumes above the quote threshold are priced at the threshold. */
export function monthlyPrice(tickets) {
  return BASE_PRICE + additionalTicketCost(Math.min(Math.max(0, tickets), QUOTE_THRESHOLD));
}

// The grid as rows: the base with its included tickets, one row per tier,
// then « on quote » beyond the threshold. `key` names the row a volume falls in.
export const gridRows = () => [
  { key: 'base', from: 0, to: INCLUDED_TICKETS, price: null },
  ...TICKET_TIERS.map((tier, index) => ({
    key: `tier-${index}`,
    from: (index === 0 ? INCLUDED_TICKETS : TICKET_TIERS[index - 1].upTo) + 1,
    to: tier.upTo,
    price: tier.price,
  })),
  { key: 'quote', from: QUOTE_THRESHOLD + 1, to: null, price: null },
];

export const rowForVolume = (tickets) => {
  const volume = Math.max(0, Number(tickets) || 0);
  return gridRows().find((row) => volume >= row.from && (row.to === null || volume <= row.to))?.key || 'base';
};

// The account's estimate for a monthly volume: null without a volume (never a
// made-up 0); « on quote » beyond the threshold, like the website.
export const estimateFor = (tickets) => {
  if (tickets === null || tickets === undefined || Number.isNaN(Number(tickets))) return null;
  const volume = Math.max(0, Math.round(Number(tickets)));
  if (volume > QUOTE_THRESHOLD) return { tickets: volume, onQuote: true, row: 'quote' };
  const extraCost = additionalTicketCost(volume);
  const total = BASE_PRICE + extraCost;
  return {
    tickets: volume,
    onQuote: false,
    row: rowForVolume(volume),
    base: BASE_PRICE,
    extraTickets: Math.max(0, volume - INCLUDED_TICKETS),
    extraCost,
    total,
    perTicket: volume > 0 ? total / volume : null,
  };
};
